import { SUPABASE_FUNCTIONS_URL } from '@/utils/apiEndpoints';
import { getSupabaseSession, SUPABASE_ANON_KEY } from '@/services/supabaseClient';

/**
 * Admin Service (shared public/settings methods).
 * Talks to the Supabase `admin` edge function.
 * - getPublicSettings: anonymous (publishable key) — used by the landing-page guards.
 * - getSettings / updateSetting: require an admin session.
 */
const ADMIN_FUNCTION_URL = `${SUPABASE_FUNCTIONS_URL}/admin`;

const adminFetch = async (path, { method = 'GET', body, token } = {}) => {
  const response = await fetch(`${ADMIN_FUNCTION_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  return response.json().catch(() => null);
};

export const adminService = {
  /**
   * Get public settings (e.g. landing page status). Reachable anonymously.
   */
  async getPublicSettings() {
    try {
      return await adminFetch('/public-settings');
    } catch (error) {
      console.error('Error fetching public settings:', error);
      return { success: false, data: { isLandingPageActive: false } };
    }
  },

  /**
   * Get all settings (admin only).
   */
  async getSettings() {
    const session = await getSupabaseSession();
    const result = await adminFetch('/settings', { token: session?.access_token });
    if (!result || result.success === false) {
      throw new Error(result?.error?.message || 'Ophalen instellingen mislukt');
    }
    return result;
  },

  /**
   * Update a setting (admin only).
   */
  async updateSetting(key, value) {
    const session = await getSupabaseSession();
    const result = await adminFetch(`/settings/${key}`, {
      method: 'PUT',
      body: { value },
      token: session?.access_token,
    });
    if (!result || result.success === false) {
      throw new Error(result?.error?.message || 'Bijwerken instelling mislukt');
    }
    return result;
  },
};
